'use client';

import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { BankCard } from '@shared/icons';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { PaymentInstrumentsResponse, PaymentMethod } from '../../types';
import { DeletePaymentMethodDialog } from './components/DeletePaymentMethodDialog/DeletePaymentMethodDialog';
import { PaymentMethodItem } from './components/PaymentMethodItem/PaymentMethodItem';

type PaymentMethodsProps = {
  namespace: string;
};

function PaymentMethods({ namespace }: PaymentMethodsProps) {
  const t = useTranslations(namespace);
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState<PaymentMethod | null>(null);
  const { data: paymentMethodsData, isLoading: isPaymentMethodsLoading } = useApiQuery<PaymentInstrumentsResponse>(API_ROUTES.MY_ACCOUNT.PAYMENT_INSTRUMENTS, {
    queryOptions: {},
    credentials: true,
  });

  const { paymentInstruments: paymentMethods } = paymentMethodsData || {};

  if (!paymentMethods || paymentMethods.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-10 px-4">
        <BankCard className="w-16 h-16 text-foreground-subtle" />
        <p className="text-foreground-subtle text-center">{t('empty_description_1')}</p>
        <p className="text-foreground-subtle text-center">{t('empty_description_2')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {paymentMethods.map(paymentMethod => (
        <PaymentMethodItem
          key={paymentMethod.id}
          {...paymentMethod}
          namespace={namespace}
          isLoading={isPaymentMethodsLoading}
          onDelete={() => setPaymentMethodToDelete(paymentMethod)}
          data-track-container="Payment Method"
          data-track-method={paymentMethod.name}
        />
      ))}
      <p className="paragraph-explanatory">{t('description')}</p>
      {paymentMethodToDelete && (
        <DeletePaymentMethodDialog
          key={paymentMethodToDelete.id}
          paymentMethod={paymentMethodToDelete}
          namespace={namespace}
          isLastPaymentMethod={paymentMethods.length === 1}
          onClose={() => setPaymentMethodToDelete(null)}
        />
      )}
    </div>
  );
}

export { PaymentMethods };
